/**
 * BLRRD paint engine — Heal (Poisson local-region solver) (Phase
 * 4.C.paint.3, Wave 9.3).
 *
 * Like Clone, but the source's *gradients* (not its absolute colour) are
 * pasted into the target, so cloning skin from one area to another blends
 * seamlessly even when the lighting differs. This is Pérez-Gangnet-Blake
 * 2003 "Poisson Image Editing" (SIGGRAPH) seamless cloning.
 *
 * Given (per RGB channel):
 *   f*  = target (the layer being healed onto)
 *   g   = source (sampled by a Clone-style offset)
 *   Ω   = the region under the brush (mask coverage > threshold)
 *   ∂Ω  = the boundary of Ω
 *
 * Solve for f inside Ω such that  Δf = Δg  with  f = f*  on ∂Ω. The
 * discrete Laplace update on a regular 4-neighbour grid is:
 *
 *   f[i] = ¼·( Σ_neighbours f  −  Σ_neighbours g  +  4·g[i] )
 *        = ¼·( Σ_neighbours f  +  Σ_neighbours (g[i] − g) )
 *
 * solved iteratively by Gauss-Seidel (in-place updates) accelerated with
 * Successive Over-Relaxation (Young 1971), ω ≈ 1.85:
 *
 *   f_new = (1−ω)·f_old + ω·gauss_seidel_update
 *
 * Iterate to a max-delta tolerance or an iteration cap (default 100; the
 * paper allows 500). Boundary pixels (where the brush mask is 0 just
 * outside Ω) hold the target value — Dirichlet condition.
 *
 * Soft brush edges: where the mask α < 1, blend the solved f back toward
 * the target:  out = α·f + (1−α)·f*.
 *
 * Provenance (clean-room from the papers — NO GIMP/GEGL source):
 *   • Pérez, Gangnet, Blake 2003 "Poisson Image Editing", SIGGRAPH.
 *   • Young 1971 "Iterative Solution of Large Linear Systems" (SOR).
 *
 * @module src/pipeline/paint/heal
 */

import { dabBounds, boundsToRegion } from './brush-ops.js';
import { bilinearSampleRGBA } from './clone.js';

export const DEFAULT_HEAL_ITERS = 100;
export const DEFAULT_HEAL_OMEGA = 1.85;     /* SOR over-relaxation factor */
export const DEFAULT_HEAL_TOL = 1e-3;       /* convergence tolerance in [0,1] */
/* Mask coverage above which a pixel is considered "inside Ω" (interior to
 * solve). Below this the pixel is a Dirichlet boundary holding f*. */
const OMEGA_THRESHOLD = 0.5;

/**
 * Solve the Poisson equation on a w×h scalar field with a guidance field
 * `g` and an interior mask. `f` is initialised to the target values
 * (which double as the Dirichlet boundary outside the interior) and solved
 * in place.
 *
 * @param {Float32Array} f       length w·h — target values in / boundary;
 *                               solved IN PLACE for interior cells.
 * @param {Float32Array} g       length w·h — guidance (source) field.
 * @param {Uint8Array} interior  length w·h — 1 = solve this cell, 0 = fixed.
 * @param {number} w
 * @param {number} h
 * @param {number} iters         max iterations
 * @param {number} omega         SOR factor
 * @param {number} tol           max-delta convergence tolerance
 * @returns {number} iterations actually run
 */
export function solvePoissonChannel(f, g, interior, w, h, iters, omega, tol) {
    const maxIters = iters | 0;
    const om = Number.isFinite(omega) ? omega : DEFAULT_HEAL_OMEGA;
    const tolerance = Number.isFinite(tol) ? tol : DEFAULT_HEAL_TOL;
    let it = 0;
    for (; it < maxIters; it++) {
        let maxDelta = 0;
        for (let y = 1; y < h - 1; y++) {
            const row = y * w;
            for (let x = 1; x < w - 1; x++) {
                const i = row + x;
                if (!interior[i]) continue;
                const iL = i - 1, iR = i + 1, iU = i - w, iD = i + w;
                /* Neighbour sum of f minus neighbour sum of g plus 4g[i]:
                 *   ¼·(Σf_n + (4g_i − Σg_n)). The guidance term is the
                 *   discrete Laplacian of g at i. */
                const sumF = f[iL] + f[iR] + f[iU] + f[iD];
                const lapG = 4 * g[i] - (g[iL] + g[iR] + g[iU] + g[iD]);
                const gs = 0.25 * (sumF + lapG);
                const old = f[i];
                /* SOR step. */
                const next = old + om * (gs - old);
                /* Guard against NaN/Inf propagation (e.g. a NaN source
                 * pixel) — leave the cell unchanged so corruption stays
                 * local (dossier §8.3 gate 5). */
                if (Number.isFinite(next)) {
                    const d = Math.abs(next - old);
                    if (d > maxDelta) maxDelta = d;
                    f[i] = next;
                }
            }
        }
        if (maxDelta < tolerance) { it++; break; }
    }
    return it;
}

/**
 * Heal a brush-region dab. Reads the source via a Clone-style offset,
 * solves the per-channel Poisson equation over the brush footprint, and
 * blends the solved result back into the layer through the brush mask.
 *
 * Heavier than the other dabs — the caller decides whether to run this per
 * dab (small brushes) or only on pointer-up (large brushes, dossier §4.1).
 *
 * @param {Float32Array} rgba   active-layer buffer (mutated in place)
 * @param {number} w
 * @param {number} h
 * @param {number} cx
 * @param {number} cy
 * @param {object} brush
 * @param {Float32Array} brush.mask
 * @param {number} brush.maskSize
 * @param {number} brush.effSize
 * @param {number} brush.flow
 * @param {Float32Array} brush.source   source buffer (Clone-style)
 * @param {number} brush.sourceW
 * @param {number} brush.sourceH
 * @param {number} brush.offsetX
 * @param {number} brush.offsetY
 * @param {number} [brush.iters]
 * @param {number} [brush.omega]
 * @param {number} [brush.tol]
 * @returns {{x:number,y:number,w:number,h:number}|null} dirty region
 */
export function stampHealDab(rgba, w, h, cx, cy, brush) {
    const { mask, maskSize, effSize, flow, source, sourceW, sourceH, offsetX, offsetY } = brush;
    if (!source) return null;
    const b = dabBounds(cx, cy, effSize, w, h);
    if (!b) return null;
    const f = flow == null ? 1 : (flow < 0 ? 0 : flow > 1 ? 1 : flow);
    if (f <= 0) return null;
    /* Pad the solve region by 1px on each side (still clipped to the
     * buffer) so the brush footprint has a boundary ring to anchor the
     * Dirichlet condition. */
    const rx0 = Math.max(0, b.x0 - 1), ry0 = Math.max(0, b.y0 - 1);
    const rx1 = Math.min(w - 1, b.x1 + 1), ry1 = Math.min(h - 1, b.y1 + 1);
    const rw = rx1 - rx0 + 1, rh = ry1 - ry0 + 1;
    if (rw < 3 || rh < 3) return boundsToRegion(b);   /* too small to solve */

    const r = effSize / 2;
    const ox = cx - r, oy = cy - r;
    const sW = sourceW || w, sH = sourceH || h;
    const N = rw * rh;

    /* Per-channel scratch fields + the coverage map. */
    const fR = new Float32Array(N), fG = new Float32Array(N), fB = new Float32Array(N);
    const gR = new Float32Array(N), gG = new Float32Array(N), gB = new Float32Array(N);
    const interior = new Uint8Array(N);
    const cover = new Float32Array(N);   /* brush coverage per local cell */

    const invSpan = effSize > 0 ? 1 / effSize : 0;

    for (let ly = 0; ly < rh; ly++) {
        const gy = ry0 + ly;
        for (let lx = 0; lx < rw; lx++) {
            const gx = rx0 + lx;
            const li = ly * rw + lx;
            const j = (gy * w + gx) * 4;
            /* Target = current layer pixel (Dirichlet boundary + init). */
            fR[li] = rgba[j]; fG[li] = rgba[j + 1]; fB[li] = rgba[j + 2];
            /* Source via the Clone-style offset (bilinear, edge-clamped). */
            const s = bilinearSampleRGBA(source, sW, sH, gx + offsetX, gy + offsetY);
            gR[li] = s[0]; gG[li] = s[1]; gB[li] = s[2];
            /* Brush coverage at this cell (normalised mask sample). The
             * solve interior is where coverage exceeds the threshold AND
             * the cell isn't on the local-grid edge (those stay Dirichlet). */
            const u = ((gx + 0.5) - ox) * invSpan;
            const v = ((gy + 0.5) - oy) * invSpan;
            const mxC = u * maskSize - 0.5;
            const myC = v * maskSize - 0.5;
            const c = sampleMaskNearest(mask, maskSize, mxC, myC);
            cover[li] = c;
            const onEdge = (lx === 0 || ly === 0 || lx === rw - 1 || ly === rh - 1);
            interior[li] = (!onEdge && c >= OMEGA_THRESHOLD) ? 1 : 0;
        }
    }

    const iters = Number.isFinite(brush.iters) ? brush.iters : DEFAULT_HEAL_ITERS;
    const omega = Number.isFinite(brush.omega) ? brush.omega : DEFAULT_HEAL_OMEGA;
    const tol = Number.isFinite(brush.tol) ? brush.tol : DEFAULT_HEAL_TOL;
    solvePoissonChannel(fR, gR, interior, rw, rh, iters, omega, tol);
    solvePoissonChannel(fG, gG, interior, rw, rh, iters, omega, tol);
    solvePoissonChannel(fB, gB, interior, rw, rh, iters, omega, tol);

    /* Blend the solved result back through the brush mask × flow. Outside
     * the interior nothing changes (boundary held f*). Inside, mix toward
     * the solved value by α = coverage·flow. */
    for (let ly = 0; ly < rh; ly++) {
        const gy = ry0 + ly;
        for (let lx = 0; lx < rw; lx++) {
            const li = ly * rw + lx;
            if (!interior[li]) continue;
            const a = cover[li] * f;
            if (a <= 0) continue;
            const gx = rx0 + lx;
            const j = (gy * w + gx) * 4;
            rgba[j]     = rgba[j]     + (fR[li] - rgba[j])     * a;
            rgba[j + 1] = rgba[j + 1] + (fG[li] - rgba[j + 1]) * a;
            rgba[j + 2] = rgba[j + 2] + (fB[li] - rgba[j + 2]) * a;
            /* Alpha untouched — heal modifies colour within existing
             * coverage, like dodge/burn. */
        }
    }
    return boundsToRegion(b);
}

/* Nearest-cell mask sample (clamped). Heal only needs a coarse coverage
 * map to define Ω, so nearest is fine and cheaper than bilinear. */
function sampleMaskNearest(mask, maskSize, mx, my) {
    if (mx <= -0.5 || mx >= maskSize - 0.5 || my <= -0.5 || my >= maskSize - 0.5) return 0;
    let x = Math.round(mx), y = Math.round(my);
    if (x < 0) x = 0; if (x > maskSize - 1) x = maskSize - 1;
    if (y < 0) y = 0; if (y > maskSize - 1) y = maskSize - 1;
    return mask[y * maskSize + x];
}

export { OMEGA_THRESHOLD };
